import {
  type GfxCommonBlockProps,
  GfxCompatible,
} from '@blocksuite/affine/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/affine/store';

export type AIChatCanvasMode = 'chat' | 'agent';

export type AIChatCanvasGoalCapability = 'disabled';

export type AIChatCanvasGenerationState = 'generating' | 'finished' | 'error';

type AIChatProps = {
  /** One visible user/assistant turn, including its cards and confirmations. */
  messages: string;
  /** Actor-owned execution session. Never used as collaborator read authority. */
  sessionId: string;
  rootWorkspaceId: string; // workspace id of root chat session
  rootDocId: string; // doc id of root chat session
  parentBlockId: string;
  createdByUserId: string;
  canvasMode: AIChatCanvasMode;
  /** Explicit future extension point. Goal execution is disabled on canvas V1. */
  goalCapability: AIChatCanvasGoalCapability;
  /** Persisted render state for an in-flight or completed canvas turn. */
  generationState: AIChatCanvasGenerationState;
  schemaVersion: number;
} & Omit<GfxCommonBlockProps, 'rotate'>;

export const AIChatBlockSchema = defineBlockSchema({
  flavour: 'affine:embed-ai-chat',
  props: (): AIChatProps =>
    ({
      index: 'a0',
      lockedBySelf: false,
      scale: 1,
      messages: '',
      sessionId: '',
      rootWorkspaceId: '',
      rootDocId: '',
      parentBlockId: '',
      createdByUserId: '',
      canvasMode: 'chat',
      goalCapability: 'disabled',
      generationState: 'finished',
      schemaVersion: 3,
    }) as AIChatProps,
  metadata: {
    version: 1,
    role: 'content',
    children: [],
  },
  toModel: () => {
    return new AIChatBlockModel();
  },
});

export const AIChatBlockSchemaExtension =
  BlockSchemaExtension(AIChatBlockSchema);

export class AIChatBlockModel extends GfxCompatible<AIChatProps>(BlockModel) {}
